import { createRouter, createWebHistory } from "vue-router";
import * as Sentry from "@sentry/vue";
import { ROUTE } from "@/router/routeDefinitions.ts";
import ErrorTechnical from "@/views/RestrictedArea/ErrorPage/ErrorTechnical.vue";
import CartErrorPage from "@/views/RestrictedArea/DocumentCheckout/CartErrorPage/CartErrorPage.vue";

// Behind the load-balancer's `least_conn` (no sticky sessions), a lazy route chunk can
// land on a container running a different build than the one that served index.html,
// so the chunk 404s. Browsers phrase that failure differently, hence the alternatives.
const CHUNK_LOAD_ERROR_PATTERN = /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i;
const CHUNK_RELOAD_FLAG = "autoq-chunk-reload-attempted";

const isChunkLoadError = (error: unknown): boolean =>
    error instanceof Error && CHUNK_LOAD_ERROR_PATTERN.test(error.message);

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            // No component: App.vue (main.ts) is already mounted as the app root and
            // renders this route's own <router-view/>. Pointing this route at App.vue
            // again mounted it a second time nested inside itself, running its
            // onMounted bootstrap (auth handshake, token validation, cart creation, ...)
            // twice on every initial load. A plain `redirect` (rather than a custom
            // beforeEnter calling router.push) also avoids corrupting browser history -
            // an imperative push from inside a guard was landing "back" outside the
            // app entirely instead of on the previous in-app page.
            name: ROUTE.INITIAL_ROUTE.NAME,
            path: ROUTE.INITIAL_ROUTE.PATH,
            redirect: { name: ROUTE.PRODUCT_SEARCH.NAME },
        },
        {
            name: ROUTE.RESTRICTED_AREA.NAME,
            path: ROUTE.RESTRICTED_AREA.PATH,
            component: () => import("@/views/RestrictedArea/RestrictedArea.vue"),
            children: [
                {
                    name: ROUTE.PRODUCT_SEARCH.NAME,
                    path: ROUTE.PRODUCT_SEARCH.PATH,
                    component: () => import("@/views/RestrictedArea/ProductSearch/ProductSearch.vue"),
                },
                {
                    name: ROUTE.PRODUCT_DETAIL.NAME,
                    path: ROUTE.PRODUCT_DETAIL.PATH,
                    component: () => import("@/views/RestrictedArea/ProductDetail/ProductDetail.vue")
                },
                {
                    name: ROUTE.OUTBOX.NAME,
                    path: ROUTE.OUTBOX.PATH,
                    component: () => import("@/views/RestrictedArea/Outbox/OutboxPage.vue")
                },
                {
                    name: ROUTE.CUSTOMER_SEARCH.NAME,
                    path: ROUTE.CUSTOMER_SEARCH.PATH,
                    component: () => import("@/views/RestrictedArea/CustomerSearch/CustomerSearchPage.vue")
                },
                {
                    name: ROUTE.CUSTOMER_DETAIL.NAME,
                    path: ROUTE.CUSTOMER_DETAIL.PATH,
                    component: () => import("@/views/RestrictedArea/CustomerDetail/CustomerDetailPage.vue")
                },
                {
                    name: ROUTE.ERROR_NOT_FOUND.NAME,
                    path: ROUTE.ERROR_NOT_FOUND.PATH,
                    component: () => import("@/views/RestrictedArea/ErrorPage/ErrorNotFound.vue")
                },
                {
                    name: ROUTE.MODULAR_QUALITY_STATEMENT_WIZARD.NAME,
                    path: ROUTE.MODULAR_QUALITY_STATEMENT_WIZARD.PATH,
                    component: () => import("@/views/RestrictedArea/ModularQualityStatementWizard/ModularQualityStatementWizard.vue"),
                    children: []
                },
                {
                    name: ROUTE.DOCUMENT_CHECKOUT.NAME,
                    path: ROUTE.DOCUMENT_CHECKOUT.PATH,
                    redirect: ROUTE.DOCUMENT_CHECKOUT_CART_OVERVIEW.PATH,
                    component: () => import("@/views/RestrictedArea/DocumentCheckout/DocumentCheckout.vue"),
                    children: [
                        {
                            name: ROUTE.DOCUMENT_CHECKOUT_CART_OVERVIEW.NAME,
                            path: ROUTE.DOCUMENT_CHECKOUT_CART_OVERVIEW.PATH,
                            component: () => import("@/views/RestrictedArea/DocumentCheckout/CartOverviewPage/CartOverviewPage.vue"),
                        },
                        {
                            name: ROUTE.DOCUMENT_CHECKOUT_CART_SUCCESS.NAME,
                            path: ROUTE.DOCUMENT_CHECKOUT_CART_SUCCESS.PATH,
                            component: () => import("@/views/RestrictedArea/DocumentCheckout/CartSuccessPage/CartSuccessPage.vue"),
                        },
                        {
                            name: ROUTE.DOCUMENT_CHECKOUT_CART_ERROR.NAME,
                            path: ROUTE.DOCUMENT_CHECKOUT_CART_ERROR.PATH,
                            // Statically imported (not lazy) so it can still be reached when
                            // navigation is triggered by a network failure - a lazy chunk fetch
                            // would fail the same way and leave the error unreported.
                            component: CartErrorPage,
                        },
                        {
                            path: ROUTE.DOCUMENT_CHECKOUT_CART_CREATE_CUSTOMER.PATH,
                            name: ROUTE.DOCUMENT_CHECKOUT_CART_CREATE_CUSTOMER.NAME,
                            component: () => import("@/views/RestrictedArea/DocumentCheckout/CartCreateCustomer/CartCreateCustomer.vue")
                        }
                    ]
                },
                {
                    name: ROUTE.ERROR_PAGE.NAME,
                    path: ROUTE.ERROR_PAGE.PATH,
                    // Statically imported: this is the last-resort fallback used when another
                    // navigation already failed, so it must not depend on its own chunk fetch.
                    component: ErrorTechnical
                },
            ]
        },

    ]
});

// A stale chunk 404 is a one-off version-skew glitch, not a real navigation error - a
// single reload picks up the container that's already on the new build. Guarded by
// sessionStorage so a persistent failure (e.g. the chunk is genuinely missing) surfaces
// as a normal error instead of reload-looping forever.
router.onError((error, to) => {
    if (isChunkLoadError(error)) {
        if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) return;

        sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
        // Navigate to the intended destination rather than reload() - a failed
        // navigation never reaches finalizeNavigation, so the URL bar is still on the
        // previous route and reload() would just reload that instead of `to`.
        window.location.assign(to.fullPath);

        return;
    }

    Sentry.captureException(error);
});

router.afterEach(() => {
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
});

export default router;
